import { apiFetch } from '@/api/client'

/** Lab Mongo Game ids used by My P/L `game` (same as CM-API `LAB_GAME_IDS`). */
export const LAB_MY_PNL_GAME_IDS = {
  sportsBook: '5b36053e46123555cca93993',
  liveCasino: '5a818572650cb46dd9b1a25a',
  cardGame: '5f311f236eae8b1e0f35e59a',
  racing: '60ed93b341bae473d53ae139',
} as const

/** Lab / Agent-API page size for getPLBySubGame. */
export const MY_PNL_PAGE_SIZE = 50

export type MyPnlFilter = {
  from: string
  to: string
  subGame: string
  eventId?: string
  typeName?: string
  page?: number
}

export type MyPnlApiRow = {
  eventId?: string
  eventName?: string
  gameName?: string
  subGame?: string
  typeName?: string
  createdAt?: string
  win?: number
  commission?: number
  netAmt?: number
  winINR?: number
  commissionINR?: number
  netAmtINR?: number
  betFairAmount?: number
  uplineCommission?: number
  downlineCommission?: number
  betCount?: number
  userCount?: number
}

export type MyPnlSum = {
  win: number
  commission: number
  netAmt: number
  winINR: number
  commissionINR: number
  netAmtINR: number
  betFairAmount: number
}

export type MyPnlListResponse = {
  status: number
  message: string
  data: {
    results: { data: MyPnlApiRow[] }
    totalSum: MyPnlSum
    totalItems: number
    maxRow: number
  }
}

export type MyPnlGrandTotalResponse = {
  status: number
  message: string
  data: MyPnlSum
}

export type MyPnlEventOption = { value: string; label: string }

export type MyPnlDrillRow = {
  id: string
  label: string
  eventName: string
  win: number
  commission: number
  netAmt: number
  winInr: number
  commissionInr: number
  netAmtInr: number
  betFairAmount: number
  uplineCommission: number
  downlineCommission: number
  betCount: number
  userCount: number
}

export type MyPnlEventRow = {
  id: string
  settledAt: string
  gameName: string
  subGame: string
  eventName: string
  eventId?: string
  win: number
  commission: number
  netAmt: number
  winInr: number
  commissionInr: number
  netAmtInr: number
  betFairAmount: number
  uplineCommission: number
  downlineCommission: number
  userCount: number
  betCount: number
  /** Inline Market/User drill row under an event (Lab `market-row` / `user-row`). */
  drill?: boolean
  /** Parent event id — drill rows sort with this event (stable sort keeps them under it). */
  drillParentId?: string
}

/** Resolve the event row a table row sorts as (drill → parent). */
export function myPnlSortTarget(
  row: MyPnlEventRow,
  byId: Map<string, MyPnlEventRow>,
): MyPnlEventRow {
  if (row.drill && row.drillParentId) return byId.get(row.drillParentId) ?? row
  return row
}

const ZERO_SUM: MyPnlSum = {
  win: 0,
  commission: 0,
  netAmt: 0,
  winINR: 0,
  commissionINR: 0,
  netAmtINR: 0,
  betFairAmount: 0,
}

/**
 * Lab `$scope.getMasterDashboardOnClick` game id from Sub Game name.
 * Extended for CM-UI Horse/Greyhound / Premium BM / Fancy.
 */
export function labMyPnlGameId(subGame: string): string {
  switch (subGame) {
    case 'All':
      return 'All'
    case 'Card Game':
    case 'CardGame':
      return LAB_MY_PNL_GAME_IDS.cardGame
    case 'Live Casino':
      return LAB_MY_PNL_GAME_IDS.liveCasino
    case 'Cricket':
    case 'Tennis':
    case 'Soccer':
      return LAB_MY_PNL_GAME_IDS.sportsBook
    case 'Horse Racing':
    case 'Greyhound Racing':
      return LAB_MY_PNL_GAME_IDS.racing
    case 'Premium Bookmaker':
    case 'Premium Fancy':
      return 'All'
    default:
      return 'All'
  }
}

/** Option value prefix when settlement has event_name but no event_id. */
export const MY_PNL_EVENT_NAME_PREFIX = 'n:'

/** CM-API My P/L POST body (Lab BigQuery Account shape). */
export function myPnlRequestBody(
  filter: MyPnlFilter,
  extras: { eventName?: string } = {},
): Record<string, unknown> {
  const subGame = filter.subGame?.trim() || 'All'
  const body: Record<string, unknown> = {
    fromDate: filter.from.slice(0, 10),
    toDate: filter.to.slice(0, 10),
    game: labMyPnlGameId(subGame),
    subGame,
    page: Math.max(1, Math.floor(filter.page ?? 1)),
  }
  const eventKey = filter.eventId?.trim()
  if (eventKey && eventKey !== 'All') {
    if (eventKey.startsWith(MY_PNL_EVENT_NAME_PREFIX)) {
      body.eventName = eventKey.slice(MY_PNL_EVENT_NAME_PREFIX.length)
    } else {
      body.eventId = eventKey
    }
  }
  const typeName = filter.typeName?.trim()
  if (typeName && typeName !== 'All') body.matchName = typeName
  const eventName = extras.eventName?.trim()
  if (eventName && eventName !== 'All') body.eventName = eventName
  return body
}

function n(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v)
    if (Number.isFinite(x)) return x
  }
  return 0
}

function s(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

export function mapMyPnlApiRow(row: MyPnlApiRow, index: number): MyPnlEventRow {
  const eventId = s(row.eventId) || undefined
  const eventName = s(row.eventName) || '—'
  const createdAt = s(row.createdAt) || ''
  return {
    id: `${eventId ?? eventName}-${createdAt}-${index}`,
    settledAt: createdAt,
    gameName: s(row.gameName) || '—',
    subGame: s(row.subGame) || '—',
    eventName,
    ...(eventId ? { eventId } : {}),
    win: n(row.win),
    commission: n(row.commission),
    netAmt: n(row.netAmt),
    winInr: n(row.winINR),
    commissionInr: n(row.commissionINR),
    netAmtInr: n(row.netAmtINR),
    betFairAmount: n(row.betFairAmount),
    uplineCommission: n(row.uplineCommission),
    downlineCommission: n(row.downlineCommission),
    userCount: n(row.userCount),
    betCount: n(row.betCount),
  }
}

function isApiOk(response: { status?: number }): boolean {
  return response.status === 200
}

export function mapMyPnlList(response: MyPnlListResponse): {
  rows: MyPnlEventRow[]
  total: number
  pageSum: MyPnlSum
  maxRow: number
} {
  if (!isApiOk(response)) {
    return { rows: [], total: 0, pageSum: { ...ZERO_SUM }, maxRow: MY_PNL_PAGE_SIZE }
  }
  const raw = response.data?.results?.data ?? []
  const rows = raw.map((r, i) => mapMyPnlApiRow(r, i))
  return {
    rows,
    total: n(response.data?.totalItems) || rows.length,
    pageSum: response.data?.totalSum ?? { ...ZERO_SUM },
    maxRow: n(response.data?.maxRow) || MY_PNL_PAGE_SIZE,
  }
}

export function mapMyPnlGrandTotal(response: MyPnlGrandTotalResponse): MyPnlSum {
  if (!isApiOk(response) || !response.data) return { ...ZERO_SUM }
  const d = response.data
  return {
    win: n(d.win),
    commission: n(d.commission),
    netAmt: n(d.netAmt),
    winINR: n(d.winINR),
    commissionINR: n(d.commissionINR),
    netAmtINR: n(d.netAmtINR),
    betFairAmount: n(d.betFairAmount),
  }
}

export function mapMyPnlEventOptions(response: {
  status: number
  data: {
    results: Array<{
      EVENTID?: string
      EVENTNAME?: string
      eventId?: string
      eventName?: string
      HAS_EVENT_ID?: string | number
    }>
  }
}): MyPnlEventOption[] {
  const opts: MyPnlEventOption[] = [{ value: 'All', label: 'All' }]
  if (!isApiOk(response)) return opts
  const seen = new Set<string>()
  for (const r of response.data?.results ?? []) {
    const name = s(r.EVENTNAME ?? r.eventName)
    if (!name) continue
    const rawId = s(r.EVENTID ?? r.eventId)
    // HAS_EVENT_ID=0 → EVENTID is the name fallback; filter must use eventName.
    const value =
      r.HAS_EVENT_ID === '0' || r.HAS_EVENT_ID === 0
        ? `${MY_PNL_EVENT_NAME_PREFIX}${name}`
        : rawId || `${MY_PNL_EVENT_NAME_PREFIX}${name}`
    if (seen.has(value)) continue
    seen.add(value)
    opts.push({ value, label: name })
  }
  return opts
}

/**
 * When getEventNameAndId is empty, keep Select Event Name usable from loaded
 * getPLBySubGame rows (eventId when present, else eventName via `n:` prefix).
 */
export function mergeMyPnlEventOptions(
  apiOpts: MyPnlEventOption[],
  rows: Array<Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'drill'>>,
): MyPnlEventOption[] {
  const opts = apiOpts.length > 0 ? [...apiOpts] : [{ value: 'All', label: 'All' }]
  if (!opts.some((o) => o.value === 'All')) opts.unshift({ value: 'All', label: 'All' })
  const seen = new Set(opts.map((o) => o.value))
  for (const r of rows) {
    if (r.drill) continue
    const name = r.eventName?.trim()
    if (!name || name === 'All') continue
    const id = r.eventId?.trim()
    const value = id && id !== 'All' ? id : `${MY_PNL_EVENT_NAME_PREFIX}${name}`
    if (seen.has(value)) continue
    seen.add(value)
    opts.push({ value, label: name })
  }
  return opts
}

function mapDrillRow(
  r: {
    _id?: string
    marketId?: string
    username?: string
    eventName?: string
    win?: number
    commission?: number
    netAmt?: number
    winINR?: number
    commissionINR?: number
    netAmtINR?: number
    betFairAmount?: number
    uplineCommission?: number
    downlineCommission?: number
    betCount?: number
    userCount?: number
  },
  index: number,
): MyPnlDrillRow {
  const label = s(r.username || r.marketId || r._id) || `row-${index}`
  return {
    id: `${label}-${index}`,
    label,
    eventName: s(r.eventName),
    win: n(r.win),
    commission: n(r.commission),
    netAmt: n(r.netAmt),
    winInr: n(r.winINR),
    commissionInr: n(r.commissionINR),
    netAmtInr: n(r.netAmtINR),
    betFairAmount: n(r.betFairAmount),
    uplineCommission: n(r.uplineCommission),
    downlineCommission: n(r.downlineCommission),
    betCount: n(r.betCount),
    userCount: n(r.userCount),
  }
}

export async function fetchMyPnlList(filter: MyPnlFilter) {
  const response = await apiFetch<Parameters<typeof mapMyPnlList>[0]>(
    '/v1/account/getPLBySubGame',
    { method: 'POST', body: JSON.stringify(myPnlRequestBody(filter)) },
  )
  return mapMyPnlList(response)
}

export async function fetchMyPnlGrandTotal(filter: MyPnlFilter) {
  const body = myPnlRequestBody({ ...filter, page: 1 })
  delete body.page
  const response = await apiFetch<Parameters<typeof mapMyPnlGrandTotal>[0]>(
    '/v1/account/getGrandTotalForMyPL',
    { method: 'POST', body: JSON.stringify(body) },
  )
  return mapMyPnlGrandTotal(response)
}

export async function fetchMyPnlEventOptions(filter: MyPnlFilter) {
  const body = myPnlRequestBody({ ...filter, page: 1, eventId: 'All' })
  delete body.page
  delete body.eventId
  const response = await apiFetch<Parameters<typeof mapMyPnlEventOptions>[0]>(
    '/v1/account/getEventNameAndId',
    { method: 'POST', body: JSON.stringify(body) },
  )
  return mapMyPnlEventOptions(response)
}

/**
 * Lab getMarketWithinEvent / getUsersWithinEvent body.
 * Sends eventId XOR eventName (never both) — ANDing both empties BQ when names drift.
 */
export function myPnlDrillRequestBody(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'subGame'>,
): Record<string, unknown> {
  const eventId = row.eventId?.trim()
  const body = myPnlRequestBody(
    {
      ...filter,
      page: 1,
      subGame: row.subGame || filter.subGame,
      eventId: eventId || 'All',
    },
    eventId ? {} : { eventName: row.eventName },
  )
  delete body.page
  return body
}

export function mapMyPnlDrillAsEventRow(d: MyPnlDrillRow, parentId: string): MyPnlEventRow {
  return {
    id: `drill-${parentId}-${d.id}`,
    settledAt: '',
    gameName: '',
    subGame: '',
    eventName: d.label,
    win: d.win,
    commission: d.commission,
    netAmt: d.netAmt,
    winInr: d.winInr,
    commissionInr: d.commissionInr,
    netAmtInr: d.netAmtInr,
    betFairAmount: d.betFairAmount,
    uplineCommission: d.uplineCommission,
    downlineCommission: d.downlineCommission,
    userCount: d.userCount,
    betCount: d.betCount,
    drill: true,
    drillParentId: parentId,
  }
}

export async function fetchMyPnlMarkets(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'subGame'>,
) {
  const response = await apiFetch<{ status?: number; data?: { results?: { data?: MyPnlDrillRow[] } } }>(
    '/v1/account/getMarketInMyPL',
    { method: 'POST', body: JSON.stringify(myPnlDrillRequestBody(filter, row)) },
  )
  if (!isApiOk(response)) return []
  return (response.data?.results?.data ?? []).map(mapDrillRow)
}

export async function fetchMyPnlUsers(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'subGame'>,
) {
  const response = await apiFetch<{ status?: number; data?: { results?: { data?: MyPnlDrillRow[] } } }>(
    '/v1/account/getUsersInMyPL',
    { method: 'POST', body: JSON.stringify(myPnlDrillRequestBody(filter, row)) },
  )
  if (!isApiOk(response)) return []
  return (response.data?.results?.data ?? []).map(mapDrillRow)
}
